import React, { useState, useRef, useEffect } from "react";
import { WHITE_LABEL_CONFIG } from "@/src_new/whiteLabel";
import {
  getThemeClass,
  getThemeComponentClasses,
} from "@/src_new/shared/lib/themeComponentClasses";
import { Button } from "@/src_new/shared/components";
import AuthIdentifierField from "./AuthIdentifierField";
import AuthMethodTabs from "./AuthMethodTabs";
import OtpCodeInput from "./OtpCodeInput";
import { X } from "lucide-react";

interface UserLoginFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSendOtp: (identifier: string, type: "email" | "phone") => void;
  onVerifyOtp: (identifier: string, otp: string, type: "email" | "phone") => void;
  isLoading?: boolean;
  errorMessage?: string;
}

const UserLoginForm: React.FC<UserLoginFormProps> = ({
  isOpen,
  onClose,
  onSendOtp,
  onVerifyOtp,
  isLoading = false,
  errorMessage,
}) => {
  const themeClasses = getThemeComponentClasses();
  const cardClasses = themeClasses.card;
  const buttonClasses = themeClasses.button;
  const [activeTab, setActiveTab] = useState<"email" | "phone">("email");
  const [identifier, setIdentifier] = useState("");
  const [step, setStep] = useState<"identifier" | "otp">("identifier");
  const [otpValues, setOtpValues] = useState(Array(6).fill(""));
  const [timer, setTimer] = useState(120); // 2 minutes in seconds
  const [isResending, setIsResending] = useState(false);
  const [otpSent, setOtpSent] = useState(false);

  const inputsRef = useRef<HTMLInputElement[]>([]);
  // Timer countdown effect
  useEffect(() => {
    let interval: NodeJS.Timeout | undefined;
    if (step === "otp" && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [step, timer]);

  // Reset timer on OTP step entry
  useEffect(() => {
    if (step === "otp") {
      setTimer(120);
    }
  }, [step, identifier, activeTab]);

  const isValidEmail = /\S+@\S+\.\S+/.test(identifier);
  const isValidPhone = identifier.trim().length >= 7;

  const isDisabled =
    step === "identifier"
      ? activeTab === "email"
        ? !isValidEmail
        : !isValidPhone
      : otpValues.join("").length !== 6;

  const handleSendOtp = (e: React.FormEvent) => {
    e.preventDefault();
    onSendOtp(identifier, activeTab);
    setStep("otp");
    setOtpSent(true);
    setTimeout(() => setOtpSent(false), 5000);
  };

  const handleVerifyOtp = (e: React.FormEvent) => {
    e.preventDefault();
    onVerifyOtp(identifier, otpValues.join(""), activeTab);
  };

  const handleOtpChange = (value: string, index: number) => {
    if (!/^[0-9]?$/.test(value)) return;

    const newOtp = [...otpValues];
    newOtp[index] = value;
    setOtpValues(newOtp);

    if (value && index < 5) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === "Backspace" && !otpValues[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handleClose = () => {
    setIdentifier("");
    setOtpValues(Array(6).fill(""));
    setStep("identifier");
    setActiveTab("email");
    setOtpSent(false);
    onClose();
  };

  const handleResend = async () => {
    setIsResending(true);
    setOtpValues(Array(6).fill(""));
    await new Promise((resolve) => setTimeout(resolve, 700)); // Simulate loader
    onSendOtp(identifier, activeTab);
    setTimer(120);
    setIsResending(false);
    inputsRef.current[0]?.focus();
  };

  if (!isOpen) return null;

  return (
    <div className="relative flex min-h-[80vh] w-full flex-col items-center justify-center bg-theme-canvas-muted px-4 py-8">
      {/* Close */}
      <Button
        onClick={handleClose}
        variant="secondary"
        className="absolute right-4 top-4 min-h-auto border-0 px-1 py-2 text-2xl text-theme-text-soft shadow-none "
      >
        <X size={20} />
      </Button>

      {/* Icon */}
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-theme-surface-soft">
        <span className="text-xl text-theme-primary">🛡️</span>
      </div>

      {/* Heading */}
      <h1 className="text-3xl font-bold mb-2">Welcome Back</h1>
      <p className="mb-6 text-center text-theme-text-soft">
        Sign in to manage your policies, quotes, and more
      </p>

      {/* Card */}
      <div
        className={`relative w-full max-w-[420px] rounded-2xl p-6 shadow-md ${getThemeClass(cardClasses.bgColor, "bg-theme-surface")} ${cardClasses.base}`}
      >
        {/* OTP Sent Success Message */}
        {otpSent && (
          <div className="absolute left-1/2 top-2 z-20 -translate-x-1/2 rounded border border-theme-success-border-soft bg-theme-success-pale px-3 py-1 text-xs font-medium text-theme-success-text-strong shadow">
            OTP sent successfully!
          </div>
        )}

        {/* Tabs */}
        {step === "identifier" && <AuthMethodTabs activeTab={activeTab} onChange={setActiveTab} />}

        <form
          onSubmit={step === "identifier" ? handleSendOtp : handleVerifyOtp}
          className="space-y-5"
        >
          {/* Identifier Step */}
          {step === "identifier" && (
            <AuthIdentifierField
              label={activeTab === "email" ? "Email Address" : "Phone Number"}
              type={activeTab === "email" ? "email" : "tel"}
              value={identifier}
              onChange={setIdentifier}
              placeholder={`Enter your ${activeTab}`}
            />
          )}

          {/* OTP Step */}
          {step === "otp" && (
            <>
              {/* Back */}
              <Button
                type="button"
                onClick={() => setStep("identifier")}
                variant="secondary"
                className="min-h-auto border-0 px-0 py-0 text-sm text-theme-text-soft shadow-none hover:bg-transparent"
              >
                ← Back
              </Button>

              {/* OTP Title */}
              <div className="text-center">
                <h2 className="text-xl font-semibold">Enter Verification Code</h2>
                <p className="mt-1 text-sm text-theme-text-soft">
                  We've sent a 6-digit OTP to{" "}
                  <span className="font-medium text-theme-text">{identifier}</span>
                </p>
              </div>

              {/* OTP Inputs */}
              <div className="mt-2">
                <OtpCodeInput
                  values={otpValues}
                  inputsRef={inputsRef}
                  onChange={handleOtpChange}
                  onKeyDown={handleKeyDown}
                />
              </div>

              {errorMessage && (
                <p className="text-center text-sm text-theme-danger">{errorMessage}</p>
              )}

              {/* Resend with timer and loader */}
              <p className="text-center text-sm text-theme-text-soft">
                Didn't receive the code?{" "}
                {timer > 0 ? (
                  <span className="ml-1 font-medium text-theme-text-placeholder">
                    Resend OTP in {Math.floor(timer / 60)}:
                    {(timer % 60).toString().padStart(2, "0")}
                  </span>
                ) : isResending ? (
                  <span className="ml-1 flex items-center gap-1 font-medium text-theme-primary">
                    <svg
                      className="h-4 w-4 animate-spin text-theme-primary"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                      ></path>
                    </svg>
                    Sending...
                  </span>
                ) : (
                  <span
                    onClick={handleResend}
                    className="ml-1 cursor-pointer font-medium text-theme-primary"
                  >
                    Resend OTP
                  </span>
                )}
              </p>
            </>
          )}

          {/* Button */}
          <Button
            type="submit"
            disabled={isDisabled || isLoading}
            className={`w-full py-3 rounded-full text-sm font-semibold transition ${buttonClasses.base} ${
              isDisabled
                ? `${cardClasses.softBgColor || "bg-theme-surface-soft"} ${cardClasses.subtitleColor || "text-theme-text-soft"}`
                : `${buttonClasses.solidBgColor || "bg-theme-primary"} ${getThemeClass(buttonClasses.solidTextColor, "text-theme-text-inverse")} hover:bg-theme-primary-hover ${buttonClasses.solid}`
            }`}
          >
            {step === "identifier" ? "Send OTP" : "Verify & Sign In"}
          </Button>

          {/* Info */}
          {step === "identifier" && (
            <p className="text-center text-sm text-theme-text-soft">
              {activeTab === "email"
                ? "We'll send a one-time verification code to your email"
                : "We'll send a one-time verification code to your phone"}
            </p>
          )}
        </form>
      </div>

      {/* Footer */}
      <p className="mt-6 text-sm text-theme-text-soft">
        {WHITE_LABEL_CONFIG.auth.otpFooterPrefix}{" "}
        <span className="cursor-pointer font-semibold text-theme-primary">
          {WHITE_LABEL_CONFIG.auth.otpFooterCta}
        </span>{" "}
        {WHITE_LABEL_CONFIG.auth.otpFooterSuffix}
      </p>
    </div>
  );
};

export default UserLoginForm;
